export type PropertyContextOption = {
  id: string;
  label: string;
  code?: string;
  opsHotelId?: string;
  opsHotelCode?: string;
  isDefault?: boolean;
};

const ACTIVE_PROPERTY_STORAGE_KEY = "xentros.activePropertyId";
/**
 * XEN-232 / XEN-218 (B-31, A-20): the PMS used to persist its own hotel id in a
 * second key and the two drifted apart — switching to TEST02 updated
 * xentros.pms.selectedHotelId while xentros.activePropertyId stayed on TEST01,
 * so POS (which scopes its own storage by the active property) and the PMS
 * disagreed about where the user was, and a deep link could render
 * "No hotel selected" under a header naming a hotel.
 *
 * There is now exactly one writer — setActivePropertyId — and it keeps the
 * legacy PMS key as a mirror so older readers cannot observe a stale value.
 * Nothing reads the mirror as a source of truth.
 */
const LEGACY_PMS_HOTEL_STORAGE_KEY = "xentros.pms.selectedHotelId";
const ACTIVE_PROPERTY_EVENT = "xentros-active-property-change";
/**
 * V2-009: the hotel is chosen per browser tab. Two tabs used to share one
 * localStorage value and the "storage" event moved every open tab to the hotel
 * another tab had just picked, so a form being edited for TEST01 silently became
 * a form for Inzell. Each tab now keeps its own choice in sessionStorage
 * (scoped to the tab); localStorage still holds the last hotel chosen anywhere,
 * which is where a NEW tab starts. A change in another tab no longer moves a
 * tab that has made its own choice.
 */
const TAB_PROPERTY_STORAGE_KEY = "xentros.tab.activePropertyId";

type ActivePropertyChangeEvent = CustomEvent<{ propertyId: string | null }>;

function hasWindow() {
  return typeof window !== "undefined";
}

function normalizeProperties(properties: PropertyContextOption[]) {
  return properties.filter((property) => property.id.trim());
}

function getFallbackPropertyId(properties: PropertyContextOption[]) {
  const normalized = normalizeProperties(properties);
  return normalized.find((property) => property.isDefault)?.id ?? normalized[0]?.id ?? null;
}

function isAllowedProperty(propertyId: string | null | undefined, properties: PropertyContextOption[]) {
  if (!propertyId) return false;
  return normalizeProperties(properties).some((property) => property.id === propertyId);
}

function readStorage(key: string) {
  if (!hasWindow()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** This tab's own selection (sessionStorage), or null when the tab has none yet or storage is blocked. */
function readTabPropertyId() {
  if (!hasWindow()) return null;
  try {
    return window.sessionStorage?.getItem(TAB_PROPERTY_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeTabPropertyId(propertyId: string | null) {
  if (!hasWindow()) return;
  try {
    if (!window.sessionStorage) return;
    if (propertyId) window.sessionStorage.setItem(TAB_PROPERTY_STORAGE_KEY, propertyId);
    else window.sessionStorage.removeItem(TAB_PROPERTY_STORAGE_KEY);
  } catch {
    // Blocked storage: the tab follows the shared value, as before.
  }
}

/**
 * The persisted active property, validated against the properties this user may
 * actually see. A stored id that is not in the list (a stale bookmark, a
 * revoked assignment, a value written by another tenant) is discarded in favour
 * of the default instead of leaving the app in a "no hotel selected" state that
 * contradicts its own header.
 */
export function resolveStoredPropertyId(
  storedId: string | null,
  legacyId: string | null,
  properties: PropertyContextOption[],
) {
  // XEN-218 (A-20), part two: a browser upgraded from the old build carries
  // two values that disagree, and the app has to pick the one the user last
  // chose. Before the fix only the PMS switcher wrote, and it wrote ONLY the
  // legacy key — so when the two keys disagree, the legacy key is the newer
  // one and it wins. Afterwards writeActivePropertyId always sets both, so a
  // disagreement can only be this leftover and the rule resolves it once.
  if (legacyId && legacyId !== storedId && isAllowedProperty(legacyId, properties)) return legacyId;

  if (isAllowedProperty(storedId, properties)) return storedId;

  // Reading a leftover value from the retired PMS key keeps a user's choice
  // across the upgrade instead of snapping them back to the default once.
  if (isAllowedProperty(legacyId, properties)) return legacyId;

  return null;
}

export function getActivePropertyId(properties: PropertyContextOption[]) {
  const fallbackId = getFallbackPropertyId(properties);
  if (!hasWindow()) return fallbackId;

  const tabId = readTabPropertyId();
  if (isAllowedProperty(tabId, properties)) return tabId;

  return resolveStoredPropertyId(
    readStorage(ACTIVE_PROPERTY_STORAGE_KEY),
    readStorage(LEGACY_PMS_HOTEL_STORAGE_KEY),
    properties,
  ) ?? fallbackId;
}

export function setActivePropertyId(propertyId: string, properties: PropertyContextOption[]) {
  const nextPropertyId = isAllowedProperty(propertyId, properties) ? propertyId : getFallbackPropertyId(properties);
  if (!hasWindow() || !nextPropertyId) return nextPropertyId;

  writeActivePropertyId(nextPropertyId);
  return nextPropertyId;
}

/**
 * The single writer. Used by setActivePropertyId and by the PMS hotel selector,
 * which picks a hotel that is not always resolvable to an entry in the
 * navigation property list; both end up writing the same two keys, so the
 * stores can no longer disagree.
 */
export function writeActivePropertyId(propertyId: string | null) {
  if (!hasWindow()) return;
  writeTabPropertyId(propertyId);
  try {
    if (!propertyId) {
      window.localStorage.removeItem(ACTIVE_PROPERTY_STORAGE_KEY);
      window.localStorage.removeItem(LEGACY_PMS_HOTEL_STORAGE_KEY);
    } else {
      window.localStorage.setItem(ACTIVE_PROPERTY_STORAGE_KEY, propertyId);
      window.localStorage.setItem(LEGACY_PMS_HOTEL_STORAGE_KEY, propertyId);
    }
  } catch {
    // Private mode / blocked storage: the selection then lives for this session
    // only, which is still one consistent value.
  }
  window.dispatchEvent(
    new CustomEvent(ACTIVE_PROPERTY_EVENT, {
      detail: { propertyId },
    })
  );
}

/**
 * The persisted active property without a property list to validate against.
 *
 * Same precedence as getActivePropertyId (XEN-218 / A-20): when the two keys
 * still disagree after an upgrade, the legacy PMS key holds the hotel the user
 * actually switched to last, because the old switcher wrote nothing else.
 */
export function readActivePropertyId() {
  const tabId = readTabPropertyId();
  if (tabId) return tabId;
  const storedId = readStorage(ACTIVE_PROPERTY_STORAGE_KEY);
  const legacyId = readStorage(LEGACY_PMS_HOTEL_STORAGE_KEY);
  if (legacyId && legacyId !== storedId) return legacyId;
  return storedId ?? legacyId;
}

export function subscribeToActiveProperty(listener: (propertyId: string | null) => void) {
  if (!hasWindow()) return () => {};

  const onPropertyChange = (event: Event) => {
    listener((event as ActivePropertyChangeEvent).detail.propertyId);
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key === ACTIVE_PROPERTY_STORAGE_KEY || event.key === LEGACY_PMS_HOTEL_STORAGE_KEY) {
      // V2-009: a tab with its own selection keeps it; only a tab that has not
      // chosen yet (or cannot keep a per-tab value) follows the other tab.
      if (readTabPropertyId()) return;
      listener(readActivePropertyId());
    }
  };

  window.addEventListener(ACTIVE_PROPERTY_EVENT, onPropertyChange);
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener(ACTIVE_PROPERTY_EVENT, onPropertyChange);
    window.removeEventListener("storage", onStorage);
  };
}

